import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  LinearProgress,
  Alert,
  Chip,
  Paper,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Timer as TimerIcon,
  Assignment as AssignmentIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface Question {
  _id: string;
  text: string;
  type: 'single_choice';
  options: Array<{
    _id: string;
    text: string;
  }>;
  order: number;
}

interface Evaluation {
  _id: string;
  id: string;
  title: string;
  description: string;
  questions: Question[];
  settings: {
    timeLimit: number;
    passThreshold: number;
    maxAttempts: number;
    showCorrectAnswers: boolean;
    showResultsImmediately: boolean;
  };
  type: 'course_final';
}

interface EvaluationResult {
  score: number;
  earnedPoints: number;
  totalPoints: number;
  passed: boolean;
  results: Array<{
    questionIndex: number;
    userAnswer: number;
    correctAnswers: number[];
    isCorrect: boolean;
    points: number;
    explanation?: string;
  }>;
}

interface LessonEvaluationDialogProps {
  evaluationId: string;
  onEvaluationComplete?: (result: EvaluationResult) => void;
  onClose?: () => void;
}

const LessonEvaluationDialog: React.FC<LessonEvaluationDialogProps> = ({
  evaluationId,
  onEvaluationComplete,
  onClose
}) => {
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<number[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isStarted, setIsStarted] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [result, setResult] = useState<EvaluationResult | null>(null);
  const [showAnswers, setShowAnswers] = useState(false);

  useEffect(() => {
    fetchEvaluation();
  }, [evaluationId]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isStarted && timeLeft > 0 && !isSubmitted) {
      timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
        if (timeLeft <= 1) {
          handleSubmit();
        }
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [timeLeft, isStarted, isSubmitted]);

  const fetchEvaluation = async () => {
    try {
      setLoading(true);
      console.log('🔍 Intentando cargar evaluación con ID:', evaluationId);
      
      if (!evaluationId) {
        setError('No se proporcionó ID de evaluación');
        return;
      }
      
      const evalDetails = await api.getEvaluation(evaluationId);
      console.log('✅ Evaluación cargada exitosamente:', evalDetails);
      setEvaluation(evalDetails);
      setTimeLeft(evalDetails.settings.timeLimit * 60); // Convertir a segundos
    } catch (err: any) {
      console.error('❌ Error cargando evaluación:', err);
      console.error('❌ Error response:', err.response);
      console.error('❌ Error data:', err.response?.data);
      
      let errorMessage = 'Error cargando evaluación';
      
      if (err.response?.status === 404) {
        errorMessage = `Evaluación no encontrada (ID: ${evaluationId})`;
      } else if (err.response?.status === 401) {
        errorMessage = 'No tienes permisos para acceder a esta evaluación';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleStart = () => {
    setIsStarted(true);
  };

  const handleAnswerChange = (questionIndex: number, answer: number) => {
    console.log('🔘 Respuesta seleccionada:', { questionIndex, answer, type: typeof answer });
    const newAnswers = [...answers];
    newAnswers[questionIndex] = answer;
    setAnswers(newAnswers);
    console.log('📝 Respuestas actualizadas:', newAnswers);
  };

  const handleNext = () => {
    if (currentQuestion < (evaluation?.questions.length || 0) - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    if (!evaluation) return;

    try {
      setIsSubmitted(true);
      console.log('📤 Enviando evaluación...');
      console.log('📤 Evaluation ID:', evaluation.id);
      console.log('📤 Evaluation _id:', evaluation._id);
      console.log('📤 Evaluation ID type:', typeof evaluation.id);
      console.log('📤 Evaluation _id type:', typeof evaluation._id);
      console.log('📤 Answers:', answers);
      console.log('📤 Time left:', timeLeft);
      console.log('📤 Evaluation object completo:', evaluation);
      
      // Usar _id si existe, sino usar id
      const evaluationId = evaluation._id || evaluation.id;
      console.log('📤 Using evaluationId:', evaluationId);
      
      const response = await api.submitEvaluation(evaluationId, answers, timeLeft);
      console.log('✅ Evaluación enviada exitosamente:', response);
      setResult(response);
      
      if (onEvaluationComplete) {
        onEvaluationComplete(response);
      }
    } catch (err: any) {
      console.error('❌ Error enviando evaluación:', err);
      console.error('❌ Error response:', err.response);
      console.error('❌ Error data:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      
      let errorMessage = 'Error enviando evaluación';
      
      if (err.response?.status === 404) {
        errorMessage = `Endpoint de evaluación no encontrado (ID: ${evaluation.id})`;
      } else if (err.response?.status === 400) {
        errorMessage = `Datos inválidos: ${err.response?.data?.message || 'Verifique las respuestas'}`;
      } else if (err.response?.status === 401) {
        errorMessage = 'No autorizado para enviar esta evaluación';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
      setIsSubmitted(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = () => {
    if (!evaluation) return 0;
    return ((currentQuestion + 1) / evaluation.questions.length) * 100;
  };

  const getAnsweredCount = () => {
    return answers.filter(answer => answer !== undefined && answer !== null).length;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Cargando evaluación...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!evaluation) {
    return (
      <Alert severity="warning">
        No se encontró evaluación
      </Alert>
    );
  }

  if (!isStarted) {
    return (
      <Box>
        <Box textAlign="center" py={3}>
          <AssignmentIcon sx={{ fontSize: 50, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {evaluation.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {evaluation.description}
          </Typography>
          
          <Box sx={{ maxWidth: 400, mx: 'auto', mb: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {evaluation.questions.length}
                </Typography>
                <Typography variant="body2">Preguntas</Typography>
              </Paper>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {evaluation.settings.timeLimit} min
                </Typography>
                <Typography variant="body2">Tiempo límite</Typography>
              </Paper>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {evaluation.settings.passThreshold}%
                </Typography>
                <Typography variant="body2">Para aprobar</Typography>
              </Paper>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {evaluation.settings.maxAttempts}
                </Typography>
                <Typography variant="body2">Intentos máx.</Typography>
              </Paper>
            </Box>
          </Box>

          <Button
            variant="contained"
            size="large"
            onClick={handleStart}
            sx={{ minWidth: 200 }}
          >
            Comenzar Evaluación
          </Button>
        </Box>
      </Box>
    );
  }

  if (result) {
    return (
      <Box>
        <Box textAlign="center" py={3}>
          {result.passed ? (
            <CheckCircleIcon sx={{ fontSize: 70, color: 'success.main', mb: 2 }} />
          ) : (
            <CancelIcon sx={{ fontSize: 70, color: 'error.main', mb: 2 }} />
          )}
          
          <Typography variant="h4" gutterBottom color={result.passed ? 'success.main' : 'error.main'}>
            {result.passed ? '¡Felicitaciones!' : 'Evaluación no aprobada'}
          </Typography>
          
          <Typography variant="h6" gutterBottom>
            Puntuación: {result.score.toFixed(1)}%
          </Typography>
          
          <Box sx={{ maxWidth: 400, mx: 'auto', mb: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {result.earnedPoints}/{result.totalPoints}
                </Typography>
                <Typography variant="body2">Puntos obtenidos</Typography>
              </Paper>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {evaluation.settings.passThreshold}%
                </Typography>
                <Typography variant="body2">Umbral de aprobación</Typography>
              </Paper>
            </Box>
          </Box>

          {evaluation.settings.showCorrectAnswers && (
            <Box>
              <Button
                variant="outlined"
                onClick={() => setShowAnswers(!showAnswers)}
                startIcon={showAnswers ? <VisibilityOffIcon /> : <VisibilityIcon />}
                sx={{ mb: 2 }}
              >
                {showAnswers ? 'Ocultar respuestas' : 'Ver respuestas correctas'}
              </Button>

              {showAnswers && (
                <Box sx={{ maxWidth: 600, mx: 'auto', textAlign: 'left' }}>
                  <Typography variant="h6" gutterBottom>
                    Respuestas correctas:
                  </Typography>
                  {result.results.map((questionResult, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Pregunta {index + 1}: {evaluation.questions[index].text}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {questionResult.isCorrect ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                        <Typography variant="body2">
                          Tu respuesta: {evaluation.questions[index].options[questionResult.userAnswer]?.text}
                        </Typography>
                      </Box>
                      {!questionResult.isCorrect && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Respuesta correcta: {evaluation.questions[index].options[questionResult.correctAnswers[0]]?.text}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              onClick={onClose}
              sx={{ mr: 2 }}
            >
              Cerrar
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  const currentQuestionData = evaluation.questions[currentQuestion];

  return (
    <Box>
      {/* Header con progreso y tiempo */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Pregunta {currentQuestion + 1} de {evaluation.questions.length}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <TimerIcon color="warning" />
            <Typography variant="h6" color={timeLeft < 300 ? 'error' : 'inherit'}>
              {formatTime(timeLeft)}
            </Typography>
          </Box>
        </Box>
        
        <LinearProgress 
          variant="determinate" 
          value={getProgressPercentage()} 
          sx={{ height: 8, borderRadius: 4 }}
        />
        
        <Box display="flex" justifyContent="space-between" mt={1}>
          <Typography variant="body2" color="text.secondary">
            Progreso: {getAnsweredCount()}/{evaluation.questions.length} respondidas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Math.round(getProgressPercentage())}% completado
          </Typography>
        </Box>
      </Box>

      {/* Pregunta actual */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          {currentQuestionData.text}
        </Typography>

        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <RadioGroup
            value={answers[currentQuestion] !== undefined ? answers[currentQuestion].toString() : ''}
            onChange={(e) => handleAnswerChange(currentQuestion, parseInt(e.target.value))}
          >
            {currentQuestionData.options.map((option, optionIndex) => (
              <FormControlLabel
                key={option._id}
                value={optionIndex}
                control={<Radio />}
                label={option.text}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1,
                  mb: 1,
                  width: '100%',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>

      {/* Navegación */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Button
          variant="outlined"
          onClick={handlePrevious}
          disabled={currentQuestion === 0}
        >
          Anterior
        </Button>

        <Box display="flex" gap={1} sx={{ flexWrap: 'wrap' }}>
          {evaluation.questions.map((_, index) => (
            <Chip
              key={index}
              label={index + 1}
              size="small"
              color={answers[index] !== undefined ? 'primary' : 'default'}
              variant={currentQuestion === index ? 'filled' : 'outlined'}
              onClick={() => setCurrentQuestion(index)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>

        {currentQuestion === evaluation.questions.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitted || getAnsweredCount() < evaluation.questions.length}
          >
            {isSubmitted ? 'Enviando...' : 'Finalizar Evaluación'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
          >
            Siguiente
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default LessonEvaluationDialog;
